import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

const AUTO_DISMISS_DELAY = 5000;

/**
 * @typedef {object} CartToastRefs
 * @property {HTMLElement} notification
 * @property {HTMLElement} media
 * @property {HTMLImageElement} image
 * @property {HTMLElement} message
 * @property {HTMLAnchorElement} link
 * @property {HTMLButtonElement} closeButton
 *
 * @extends {Component<CartToastRefs>}
 */
class CartToastComponent extends Component {
  requiredRefs = ['notification', 'media', 'image', 'message', 'link', 'closeButton'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | undefined} */
  #hideTimeout;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | null} */
  #scrollRafId = null;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAdd, { signal });
    this.refs.closeButton.addEventListener('click', this.hide, { signal });
    this.refs.link.addEventListener('click', this.#handleLinkClick, { signal });
    this.refs.notification.addEventListener('transitionend', this.#handleTransitionEnd, { signal });

    window.addEventListener('resize', this.#updateOffset, { signal });
    document.addEventListener('scroll', this.#handleScroll, { signal });

    const headerGroup = document.querySelector('#header-group');
    if (headerGroup instanceof HTMLElement) {
      this.#resizeObserver = new ResizeObserver(() => this.#updateOffset());
      this.#resizeObserver.observe(headerGroup);
    }

    this.#updateOffset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    this.#clearHideTimeout();
  }

  /**
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { source?: string, didError?: boolean, productTitle?: string, productImage?: string } }>} event
   */
  #handleCartAdd = (event) => {
    const eventData = event.detail?.data;

    if (eventData?.source !== 'product-form-component' || eventData.didError) return;

    const addedToCart = Theme.translations?.added_to_cart || Theme.translations?.added || 'Added to cart';
    const viewCart = Theme.translations?.view_cart || 'View cart';
    const cartCount = event.detail?.resource?.item_count;
    const productTitle = eventData.productTitle?.trim();
    const productImage = eventData.productImage?.trim();

    this.refs.message.textContent = productTitle ? `${productTitle} ${addedToCart}` : addedToCart;
    this.refs.link.textContent = cartCount ? `${viewCart} (${cartCount})` : viewCart;
    this.refs.link.href = Theme.routes.cart_url;

    if (productImage) {
      this.refs.image.src = productImage;
      this.refs.image.alt = productTitle || Theme.translations?.placeholder_image || '';
      this.refs.media.hidden = false;
    } else {
      this.refs.image.removeAttribute('src');
      this.refs.image.alt = '';
      this.refs.media.hidden = true;
    }

    this.show();
  };

  /**
   * Opens the existing cart drawer instead of navigating away when available.
   * Falls back to the cart page href if the drawer is not present.
   *
   * @param {MouseEvent} event
   */
  #handleLinkClick = (event) => {
    const cartDrawer = document.querySelector('cart-drawer-component');

    if (!(cartDrawer instanceof HTMLElement) || typeof cartDrawer.open !== 'function') return;

    event.preventDefault();
    cartDrawer.open();
    this.hide();
  };

  show() {
    this.#clearHideTimeout();
    this.#updateOffset();
    this.refs.notification.hidden = false;

    requestAnimationFrame(() => {
      this.refs.notification.dataset.visible = 'true';
    });

    this.#hideTimeout = window.setTimeout(() => {
      this.hide();
    }, AUTO_DISMISS_DELAY);
  }

  hide = () => {
    this.#clearHideTimeout();

    if (this.refs.notification.hidden) return;

    this.refs.notification.dataset.visible = 'false';
  };

  /**
   * @param {TransitionEvent} event
   */
  #handleTransitionEnd = (event) => {
    if (event.target !== this.refs.notification) return;

    if (this.refs.notification.dataset.visible !== 'true') {
      this.refs.notification.hidden = true;
    }
  };

  #clearHideTimeout() {
    if (this.#hideTimeout) {
      clearTimeout(this.#hideTimeout);
      this.#hideTimeout = undefined;
    }
  }

  #handleScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateOffset();
    });
  };

  #updateOffset = () => {
    const headerGroup = document.querySelector('#header-group');
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const minTop = isMobile ? 12 : 16;
    const gap = isMobile ? 8 : 10;

    if (!(headerGroup instanceof HTMLElement)) {
      this.style.setProperty('--cart-toast-top', `${minTop}px`);
      return;
    }

    const visibleChildBottoms = Array.from(headerGroup.children)
      .filter((child) => child instanceof HTMLElement)
      .map((child) => child.getBoundingClientRect())
      .filter((rect) => rect.height > 0 && rect.bottom > 0)
      .map((rect) => rect.bottom);

    const headerBottom = visibleChildBottoms.length > 0 ? Math.max(...visibleChildBottoms) : 0;
    let obstructionBottom = headerBottom;

    if (isMobile) {
      const collectionPillsNav = document.querySelector('[data-testid="collection-pills-nav"]');

      if (collectionPillsNav instanceof HTMLElement) {
        const collectionPillsNavRect = collectionPillsNav.getBoundingClientRect();
        const isTopObstacle =
          collectionPillsNavRect.height > 0 &&
          collectionPillsNavRect.bottom > headerBottom &&
          collectionPillsNavRect.top <= headerBottom + 64;

        if (isTopObstacle) {
          obstructionBottom = Math.max(obstructionBottom, collectionPillsNavRect.bottom);
        }
      }
    }

    const nextTop = Math.max(minTop, Math.round(obstructionBottom + gap));

    this.style.setProperty('--cart-toast-top', `${nextTop}px`);
  };
}

if (!customElements.get('cart-toast-component')) {
  customElements.define('cart-toast-component', CartToastComponent);
}
